import { cn } from 'utils/cn.tsx';
import { forwardRef, type ComponentPropsWithRef, type ElementType, type ForwardedRef } from 'preact/compat';

export type PillType = 'primary' | 'secondary' | 'secondaryBackground' | 'tertiary' | 'quaternary' | 'faded' | 'action';

type Props<T extends ElementType> = {
	as?: T;
} & ComponentPropsWithRef<T>;

export const Pill = forwardRef(function Pill<T extends ElementType = 'button'>(
	{
		as: Component = 'button',
		children,
		isActive,
		pillType = 'secondary',
		className,
		astroReload,
		...props
	}: Props<T>,
	ref:
		| ForwardedRef<T>
		// workaround for T not resolving to the correct type of HTMLElement
		| ForwardedRef<HTMLButtonElement>
		| ForwardedRef<HTMLAnchorElement>
		| ForwardedRef<HTMLDivElement>
		| ForwardedRef<HTMLLabelElement>,
) {
	return (
		<Component
			className={cn(
				'inline-block text-sm rounded-full px-5 py-2 font-medium border-solid transition-colors [$>.icon]:transition-colors',
				pillType === 'primary' &&
					'bg-red text-white hover:bg-red-hover hover:text-white focus:text-white focus:bg-red-focus disabled:bg-gray-disabled-background font-bold',
				pillType === 'primary' && isActive && 'bg-red-hover',
				pillType === 'secondary' &&
					'font-bold border border-gray text-dark-blue hover:border-dark-blue focus:border-dark-blue [&>.icon]:text-dark-blue-25 [&:hover>.icon]:text-dark-blue [&:focus>.icon]:text-dark-blue',
				pillType === 'secondary' && isActive && 'font-bold border-dark-blue [&>.icon]:text-dark-blue',
				pillType === 'secondaryBackground' &&
					'border border-dark-blue text-dark-blue hover:text-white hover:bg-dark-blue focus:text-white focus:bg-dark-blue',
				pillType === 'secondaryBackground' && isActive && 'bg-neutral-10 font-bold',
				pillType === 'tertiary' &&
					'border border-gray border-solid bg-white shadow-lg text-dark-blue hover:text-red focus:text-red-hover',
				pillType === 'tertiary' && isActive && 'text-red',
				pillType === 'faded' &&
					'border border-gray text-dark-blue-70 hover:text-dark-blue hover:border-dark-blue focus:text-dark-blue focus:border-dark-blue [&>.icon]:text-dark-blue-25 [&:hover>.icon]:text-dark-blue [&:focus>.icon]:text-dark-blue',
				pillType === 'faded' && isActive && 'bg-light-gray font-bold border-transparent',
				pillType === 'quaternary' &&
					'border border-transparent hover:text-dark-blue hover:border-dark-blue focus:text-dark-blue focus:border-dark-blue',
				pillType === 'quaternary' &&
					(isActive ? 'bg-light-gray font-bold border-transparent' : 'text-neutral-65'),
				pillType === 'action' &&
					'text-dark-blue font-bold [&>.icon]:text-dark-blue-25 [&:hover>.icon]:text-dark-blue [&:focus>.icon]:text-dark-blue px-0.5 py-2',
				pillType === 'actionWithHover' &&
					'text-dark-blue font-bold [&>.icon]:text-dark-blue hover:text-dark-blue-25 [&:hover>.icon]:text-dark-blue-25 [&:focus>.icon]:text-dark-blue-25 px-0.5 py-2',
				className,
			)}
			{...props}
			ref={ref}
			data-astro-reload={astroReload}
		>
			{children}
		</Component>
	);
});
